import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExperiments } from "@/hooks/useFirestore";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { formatTrialDateRange } from "@/lib/utils";
import { Trial } from "@/types/trial";
import { Calendar, Edit, FlaskConical, MapPin, MoreHorizontal, Trash2 } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

interface TrialCardProps {
  trial: Trial;
  onDeleteTrial: (trialId: string) => void;
  onEditTrial: (trial: Trial) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const TrialCard: React.FC<TrialCardProps> = ({ trial, onDeleteTrial, onEditTrial, canEdit = false, canDelete = false }) => {
  const { experiments, loading: experimentsLoading } = useExperiments(trial.id);
  const paths = useOrgPaths();

  // Get experiment count
  const experimentCount = experiments.length;
  const preventCardNavigation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="relative h-full">
      <Link
        to={paths.trial(trial.id)}
        className="block h-full">
        <Card className="group hover:shadow-md hover:border-primary/20 transition-all duration-200 flex flex-col h-full">
          {/* Header: Title + Menu */}
          <div className="p-5 pb-0">
            <div className="flex justify-between items-start gap-3">
              <h3 className="text-lg font-semibold text-foreground leading-snug line-clamp-2 flex-1">
                {trial.name}
              </h3>
              <div
                onClick={preventCardNavigation}
                className="flex-shrink-0 -mt-1 -mr-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-32">
                    {canEdit && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditTrial(trial);
                        }}
                        className="cursor-pointer">
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteTrial(trial.id);
                          }}
                          className="text-destructive focus:text-destructive cursor-pointer">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Content: Crops + Description */}
          <div className="px-5 pt-3 flex-1">
            {/* Crops - prominent badges */}
            {trial.crops && trial.crops.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {trial.crops.slice(0, 3).map((crop, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="bg-primary/10 text-primary border-primary/30 text-xs px-2.5 py-0.5 font-medium">
                    {crop}
                  </Badge>
                ))}
                {trial.crops.length > 3 && (
                  <Badge
                    variant="outline"
                    className="bg-muted text-muted-foreground border-transparent text-xs px-2.5 py-0.5">
                    +{trial.crops.length - 3}
                  </Badge>
                )}
              </div>
            )}

            {/* Description - reserves consistent space */}
            <div className="min-h-[40px]">
              {trial.description && (
                <p className="text-sm text-muted-foreground/80 line-clamp-2 leading-relaxed">
                  {trial.description}
                </p>
              )}
            </div>
          </div>

          {/* Footer: Metadata row */}
          <div className="px-5 py-4 mt-auto border-t border-border/40">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{formatTrialDateRange(trial)}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{trial.locationName || "No location"}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <FlaskConical className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  {experimentsLoading
                    ? "..."
                    : `${experimentCount} experiment${experimentCount !== 1 ? "s" : ""}`}
                </span>
              </span>
            </div>
          </div>
        </Card>
      </Link>
    </div>
  );
};

export default TrialCard;
